/**
 * LinkedIn Carousel Generator
 * Creates shareable carousel slides from AI-generated insights
 * Integrated with AI Insights feature for dynamic content
 */
import { useState, useRef, useCallback, useEffect } from "react";
import html2canvas from "html2canvas";
import axios from "axios";
import { Download, ChevronLeft, ChevronRight, Share2, Sparkles, Check, Copy, RefreshCw, Loader2 } from "lucide-react";
import { API } from "../../lib/constants";

// Carousel slide templates
const SLIDE_TEMPLATES = {
  title: {
    id: "title",
    name: "Title Slide",
    bgGradient: "from-indigo-900 via-purple-900 to-indigo-900"
  },
  stats: {
    id: "stats",
    name: "Key Stats",
    bgGradient: "from-slate-900 via-slate-800 to-slate-900"
  },
  finding: {
    id: "finding",
    name: "Key Finding",
    bgGradient: "from-emerald-900 via-teal-900 to-emerald-900"
  },
  chart: {
    id: "chart",
    name: "Data Visualization",
    bgGradient: "from-blue-900 via-indigo-900 to-blue-900"
  },
  insight: {
    id: "insight",
    name: "AI Insight",
    bgGradient: "from-violet-900 via-purple-900 to-violet-900"
  },
  cta: {
    id: "cta",
    name: "Call to Action",
    bgGradient: "from-rose-900 via-pink-900 to-rose-900"
  }
};

// Generate slides from stats, research data, and AI insights
const generateSlides = (stats, researchStats, aiInsights) => {
  if (!stats) return [];

  const slides = [];
  const careCount = stats.orientation_distribution?.care || 0;
  const totalApps = stats.total_apps || 1;
  const carePercent = Math.round((careCount / totalApps) * 100);
  const countriesCount = stats.num_countries || Object.keys(stats.country_distribution || {}).length;

  // Title slide
  slides.push({
    type: "title",
    template: SLIDE_TEMPLATES.title,
    content: {
      title: "Observing How Humanity is Utilizing AI",
      subtitle: aiInsights?.headline || "Patterns in AI Product Development",
      source: "AI Orientation Observatory",
      metric: `Analyzing ${totalApps.toLocaleString()} AI Apps from ${countriesCount} Countries`
    }
  });

  // AI-generated flashcard slides (if available)
  if (aiInsights?.flashcards && aiInsights.flashcards.length > 0) {
    // Add up to 3 flashcard insights as slides
    aiInsights.flashcards.slice(0, 3).forEach((flashcard, idx) => {
      slides.push({
        type: "insight",
        template: SLIDE_TEMPLATES.insight,
        content: {
          title: flashcard.title || `Insight ${idx + 1}`,
          stat: flashcard.stat || "",
          insight: flashcard.insight || "",
          category: flashcard.category || "trend"
        }
      });
    });
  } else {
    // Fallback: Key stats slide if no AI insights
    slides.push({
      type: "stats",
      template: SLIDE_TEMPLATES.stats,
      content: {
        title: "The Numbers",
        stats: [
          { label: "AI Apps Tracked", value: totalApps.toLocaleString() },
          { label: "Countries", value: countriesCount.toString() },
          { label: "Care-Centered Apps", value: careCount.toLocaleString() },
          { label: "Care Percentage", value: `${carePercent}%` }
        ]
      }
    });
  }

  // Key finding slide
  const keyFinding = aiInsights?.key_takeaways?.[0] || 
    `${carePercent}% of AI apps globally are designed with human wellbeing as the primary focus`;
  
  slides.push({
    type: "finding",
    template: SLIDE_TEMPLATES.finding,
    content: {
      title: "Key Finding",
      finding: keyFinding,
      insight: "Care-centered AI prioritizes health, education, accessibility, and community support",
      icon: "heart"
    }
  });

  // Orientation distribution chart slide
  const orientations = stats.orientation_distribution || {};
  slides.push({
    type: "chart",
    template: SLIDE_TEMPLATES.chart,
    content: {
      title: "Value Orientations",
      subtitle: "How AI apps serve humanity",
      data: [
        { label: "Care", value: orientations.care || 0, color: "#10B981", desc: "Health, education, wellbeing" },
        { label: "Utility", value: orientations.utility || 0, color: "#3B82F6", desc: "Productivity, efficiency" },
        { label: "Creative", value: orientations.creative || 0, color: "#8B5CF6", desc: "Art, design, expression" },
        { label: "Extraction", value: orientations.extraction || 0, color: "#EF4444", desc: "Attention, data capture" }
      ]
    }
  });

  // Research stats slide if available
  if (researchStats?.key_findings) {
    const femaleCare = researchStats.key_findings.female_founders_care_oriented;
    if (femaleCare && femaleCare.n > 0) {
      slides.push({
        type: "finding",
        template: SLIDE_TEMPLATES.finding,
        content: {
          title: "Research Insight",
          finding: `Female-founded AI apps show ${femaleCare.point_estimate} care-orientation rate`,
          insight: femaleCare.interpretation || "Statistical analysis with 95% confidence interval",
          icon: "trending-up"
        }
      });
    }
  }

  // Additional AI insight slide if we have more takeaways
  if (aiInsights?.key_takeaways && aiInsights.key_takeaways.length > 1) {
    slides.push({
      type: "insight",
      template: SLIDE_TEMPLATES.insight,
      content: {
        title: "Data Insight",
        stat: "",
        insight: aiInsights.key_takeaways[1],
        category: "analysis"
      }
    });
  }

  // CTA slide
  slides.push({
    type: "cta",
    template: SLIDE_TEMPLATES.cta,
    content: {
      title: "Explore the Data",
      cta: "See how AI is shaping our world",
      subtitle: "Browse the full dataset and contribute your findings",
      url: "aioo.app"
    }
  });

  return slides;
};

// Individual slide component
const CarouselSlide = ({ slide, slideNumber, totalSlides, forExport = false }) => {
  const { type, template, content } = slide;
  
  const baseClasses = `bg-gradient-to-br ${template.bgGradient} text-white`;
  const sizeClasses = forExport 
    ? "w-[1080px] h-[1350px]" 
    : "w-full aspect-[4/5]";

  return (
    <div 
      className={`${baseClasses} ${sizeClasses} rounded-xl overflow-hidden relative flex flex-col`}
      data-testid={`carousel-slide-${slideNumber}`}
    >
      {/* Decorative elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
      </div>

      {/* Content */}
      <div className={`relative z-10 flex-1 flex flex-col ${forExport ? 'p-16' : 'p-6 sm:p-8'}`}>
        {/* Title Slide */}
        {type === "title" && (
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <div className={`${forExport ? 'mb-8' : 'mb-4'}`}>
              <Sparkles className={`${forExport ? 'w-20 h-20' : 'w-12 h-12'} text-yellow-400 mx-auto`} />
            </div>
            <h1 className={`font-bold leading-tight ${forExport ? 'text-7xl mb-8' : 'text-2xl sm:text-3xl mb-4'}`}>
              {content.title}
            </h1>
            <p className={`text-white/80 ${forExport ? 'text-4xl mb-12' : 'text-base sm:text-lg mb-6'}`}>
              {content.subtitle}
            </p>
            <div className={`bg-white/10 backdrop-blur-sm rounded-full ${forExport ? 'px-10 py-4' : 'px-4 py-2'}`}>
              <span className={`font-medium ${forExport ? 'text-3xl' : 'text-sm'}`}>{content.metric}</span>
            </div>
          </div>
        )}

        {/* Stats Slide */}
        {type === "stats" && (
          <div className="flex-1 flex flex-col">
            <h2 className={`font-bold ${forExport ? 'text-6xl mb-16' : 'text-2xl sm:text-3xl mb-6'}`}>
              {content.title}
            </h2>
            <div className="flex-1 grid grid-cols-2 gap-4 sm:gap-6">
              {content.stats.map((stat, idx) => (
                <div 
                  key={idx} 
                  className={`bg-white/10 backdrop-blur-sm rounded-xl flex flex-col items-center justify-center ${forExport ? 'p-10' : 'p-4'}`}
                >
                  <span className={`font-bold text-yellow-400 ${forExport ? 'text-7xl mb-4' : 'text-2xl sm:text-3xl mb-1'}`}>
                    {stat.value}
                  </span>
                  <span className={`text-white/70 text-center ${forExport ? 'text-2xl' : 'text-xs sm:text-sm'}`}>
                    {stat.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* AI Insight Slide */}
        {type === "insight" && (
          <div className="flex-1 flex flex-col justify-center">
            <div className={`flex items-center gap-3 ${forExport ? 'mb-8' : 'mb-3'}`}>
              <Sparkles className={`${forExport ? 'w-10 h-10' : 'w-5 h-5'} text-yellow-400`} />
              <span className={`text-yellow-400 font-medium uppercase tracking-wide ${forExport ? 'text-2xl' : 'text-xs'}`}>
                AI-Generated Insight
              </span>
            </div>
            <h2 className={`font-bold ${forExport ? 'text-5xl mb-8' : 'text-xl sm:text-2xl mb-4'}`}>
              {content.title}
            </h2>
            {content.stat && (
              <div className={`text-yellow-400 font-bold ${forExport ? 'text-8xl mb-8' : 'text-4xl sm:text-5xl mb-4'}`}>
                {content.stat}
              </div>
            )}
            <div className={`bg-white/10 backdrop-blur-sm rounded-xl ${forExport ? 'p-12' : 'p-4 sm:p-6'}`}>
              <p className={`font-medium leading-relaxed ${forExport ? 'text-4xl' : 'text-base sm:text-lg'}`}>
                {content.insight}
              </p>
            </div>
          </div>
        )}

        {/* Finding Slide */}
        {type === "finding" && (
          <div className="flex-1 flex flex-col justify-center">
            <h2 className={`font-bold ${forExport ? 'text-5xl mb-12' : 'text-xl sm:text-2xl mb-4'}`}>
              {content.title}
            </h2>
            <div className={`bg-white/10 backdrop-blur-sm rounded-xl ${forExport ? 'p-12 mb-10' : 'p-4 sm:p-6 mb-4'}`}>
              <p className={`font-semibold leading-relaxed ${forExport ? 'text-4xl' : 'text-base sm:text-lg'}`}>
                {content.finding}
              </p>
            </div>
            <p className={`text-white/70 ${forExport ? 'text-2xl' : 'text-sm'}`}>
              {content.insight}
            </p>
          </div>
        )}

        {/* Chart Slide */}
        {type === "chart" && (
          <div className="flex-1 flex flex-col">
            <h2 className={`font-bold ${forExport ? 'text-5xl mb-4' : 'text-xl sm:text-2xl mb-2'}`}>
              {content.title}
            </h2>
            <p className={`text-white/70 ${forExport ? 'text-2xl mb-10' : 'text-sm mb-4'}`}>
              {content.subtitle}
            </p>
            <div className="flex-1 flex flex-col justify-center gap-3 sm:gap-4">
              {content.data.map((item, idx) => {
                const total = content.data.reduce((sum, d) => sum + d.value, 0);
                const percent = total > 0 ? Math.round((item.value / total) * 100) : 0;
                return (
                  <div key={idx} className="flex items-center gap-3">
                    <div className={`font-bold ${forExport ? 'w-32 text-3xl' : 'w-16 sm:w-20 text-sm'}`}>
                      {item.label}
                    </div>
                    <div className="flex-1 h-8 sm:h-10 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-500 flex items-center justify-end pr-3"
                        style={{ width: `${Math.max(percent, 5)}%`, backgroundColor: item.color }}
                      >
                        <span className={`font-bold text-white ${forExport ? 'text-xl' : 'text-xs'}`}>
                          {percent}%
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* CTA Slide */}
        {type === "cta" && (
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <h2 className={`font-bold ${forExport ? 'text-7xl mb-10' : 'text-2xl sm:text-3xl mb-4'}`}>
              {content.title}
            </h2>
            <p className={`text-white/90 ${forExport ? 'text-4xl mb-16' : 'text-lg sm:text-xl mb-8'}`}>
              {content.cta}
            </p>
            <div className={`bg-white text-slate-900 font-bold rounded-full ${forExport ? 'px-16 py-8 text-4xl' : 'px-6 py-3 text-base'}`}>
              {content.url}
            </div>
            <p className={`text-white/60 ${forExport ? 'mt-10 text-2xl' : 'mt-4 text-sm'}`}>
              {content.subtitle}
            </p>
          </div>
        )}
      </div>

      {/* Slide number indicator */}
      <div className={`absolute ${forExport ? 'bottom-8 right-10' : 'bottom-3 right-4'}`}>
        <span className={`text-white/40 font-medium ${forExport ? 'text-2xl' : 'text-xs'}`}>
          {slideNumber} / {totalSlides}
        </span>
      </div>

      {/* Branding */}
      <div className={`absolute ${forExport ? 'bottom-8 left-10 right-10' : 'bottom-3 left-4 right-4'} flex items-center justify-between`}>
        <span className={`text-white/50 font-medium ${forExport ? 'text-lg' : 'text-[10px]'}`}>
          amplifyingconsciousinnovation.com
        </span>
        <span className={`text-white/40 ${forExport ? 'text-lg' : 'text-[10px]'}`}>
          &copy; {new Date().getFullYear()} AI Orientation Observatory
        </span>
      </div>
    </div>
  );
};

// Main component
export const LinkedInCarouselGenerator = ({ stats, researchStats, preloadedInsights = null }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [copied, setCopied] = useState(false);
  const [aiInsights, setAiInsights] = useState(preloadedInsights);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState(null);
  const exportRef = useRef(null);

  // Update insights when preloaded insights change
  useEffect(() => {
    if (preloadedInsights) {
      setAiInsights(preloadedInsights);
    }
  }, [preloadedInsights]);

  // Only fetch if no preloaded insights provided
  const fetchAiInsights = useCallback(async () => {
    if (preloadedInsights) return; // Don't fetch if we have preloaded insights
    
    setInsightsLoading(true);
    setInsightsError(null);
    try {
      const response = await axios.post(`${API}/insights`, {
        query: "Generate compelling insights about how humanity is using AI, focusing on care-centered apps, geographic distribution, and founder diversity patterns"
      });
      setAiInsights(response.data);
    } catch (error) {
      void 0; // error handled silently
      setInsightsError("Could not generate AI insights. Using default content.");
    } finally {
      setInsightsLoading(false);
    }
  }, [preloadedInsights]);

  useEffect(() => {
    if (stats && !aiInsights && !insightsLoading && !preloadedInsights) {
      fetchAiInsights();
    }
  }, [stats, aiInsights, insightsLoading, fetchAiInsights, preloadedInsights]);

  const slides = generateSlides(stats, researchStats, aiInsights);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  // Download all slides as a ZIP-like bundle (individual PNGs)
  const downloadAllSlides = useCallback(async () => {
    setIsGenerating(true);
    setDownloadProgress(0);

    try {
      for (let i = 0; i < slides.length; i++) {
        setDownloadProgress(Math.round(((i + 1) / slides.length) * 100));
        setCurrentSlide(i);
        
        // Wait for render
        await new Promise(resolve => setTimeout(resolve, 400));

        const canvas = await html2canvas(exportRef.current, {
          scale: 2,
          backgroundColor: null,
          useCORS: true,
          logging: false
        });

        const dataUrl = canvas.toDataURL("image/png");
        
        // Download the image
        const link = document.createElement("a");
        link.download = `aioo-carousel-slide-${i + 1}.png`;
        link.href = dataUrl;
        link.click();

        // Small delay between downloads
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    } catch (error) {
      void 0; // error handled silently
    } finally {
      setIsGenerating(false);
      setDownloadProgress(0);
    }
  }, [slides.length]);

  // Download current slide only
  const downloadCurrentSlide = useCallback(async () => {
    setIsGenerating(true);
    try {
      const canvas = await html2canvas(exportRef.current, {
        scale: 2,
        backgroundColor: null,
        useCORS: true,
        logging: false
      });

      const dataUrl = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.download = `aioo-carousel-slide-${currentSlide + 1}.png`;
      link.href = dataUrl;
      link.click();
    } catch (error) {
      void 0; // error handled silently
    } finally {
      setIsGenerating(false);
    }
  }, [currentSlide]);

  // Generate dynamic caption from AI insights
  const generateCaption = () => {
    const headline = aiInsights?.headline || "Observing how humanity is utilizing AI";
    const takeaways = aiInsights?.key_takeaways || [];
    const keyInsight = takeaways[0] || `${Math.round(((stats?.orientation_distribution?.care || 0) / (stats?.total_apps || 1)) * 100)}% of AI apps globally are care-centered`;
    
    return `${headline} 🔍

${stats?.total_apps?.toLocaleString() || "1,000+"} apps analyzed from ${Object.keys(stats?.country_distribution || {}).length} countries

Key insight: ${keyInsight}

${takeaways.length > 1 ? `Also: ${takeaways[1]}\n` : ''}
Explore the full dataset at aioo.app

#AI #Technology #AIResearch #DataScience #Innovation`;
  };

  // Copy caption to clipboard
  const copyCaption = () => {
    navigator.clipboard.writeText(generateCaption());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (slides.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
        Loading data...
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="linkedin-carousel-generator">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Share2 className="w-5 h-5 text-primary" />
            LinkedIn Carousel Generator
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {preloadedInsights ? (
              <span className="flex items-center gap-2">
                <Sparkles className="w-3 h-3 text-yellow-500" />
                Using your generated insights
              </span>
            ) : insightsLoading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" />
                Generating AI insights...
              </span>
            ) : aiInsights ? (
              <span className="flex items-center gap-2">
                <Sparkles className="w-3 h-3 text-yellow-500" />
                Powered by AI-generated insights
              </span>
            ) : (
              "Create shareable carousel posts from research insights"
            )}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {!preloadedInsights && (
            <button
              onClick={fetchAiInsights}
              disabled={insightsLoading}
              className="px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-50"
              data-testid="regenerate-insights-btn"
            >
              <RefreshCw className={`w-4 h-4 ${insightsLoading ? 'animate-spin' : ''}`} />
              {insightsLoading ? "Generating..." : "New Insights"}
            </button>
          )}
          <button
            onClick={copyCaption}
            className="px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
            data-testid="copy-caption-btn"
          >
            {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
            {copied ? "Copied!" : "Copy Caption"}
          </button>
          <button
            onClick={downloadCurrentSlide}
            disabled={isGenerating}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-primary/90 disabled:opacity-50 transition-colors"
            data-testid="download-slide-btn"
          >
            <Download className="w-4 h-4" />
            Download Slide
          </button>
          <button
            onClick={downloadAllSlides}
            disabled={isGenerating}
            className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg text-sm font-medium flex items-center gap-2 hover:opacity-90 disabled:opacity-50 transition-all"
            data-testid="download-all-btn"
          >
            <Download className="w-4 h-4" />
            {isGenerating ? `Generating ${downloadProgress}%...` : "Download All"}
          </button>
        </div>
      </div>

      {/* Error message */}
      {insightsError && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-200 rounded-lg px-4 py-3 text-sm">
          {insightsError}
        </div>
      )}

      {/* Preview Area */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Slide Preview */}
        <div className="relative">
          <div className="bg-card border border-border rounded-xl p-4">
            <CarouselSlide 
              slide={slides[currentSlide]} 
              slideNumber={currentSlide + 1} 
              totalSlides={slides.length}
            />
            
            {/* Navigation */}
            <div className="flex items-center justify-between mt-4">
              <button
                onClick={prevSlide}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
                data-testid="prev-slide-btn"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              
              <div className="flex gap-2">
                {slides.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentSlide(idx)}
                    className={`w-2.5 h-2.5 rounded-full transition-all ${
                      idx === currentSlide 
                        ? "bg-primary w-6" 
                        : "bg-muted hover:bg-muted-foreground/50"
                    }`}
                    data-testid={`slide-dot-${idx}`}
                  />
                ))}
              </div>
              
              <button
                onClick={nextSlide}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
                data-testid="next-slide-btn"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Instructions & Caption */}
        <div className="space-y-4">
          {/* AI Insights Preview */}
          {aiInsights && (
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-yellow-500" />
                AI-Generated Content
              </h3>
              <div className="space-y-3">
                <div>
                  <span className="text-xs text-muted-foreground uppercase tracking-wide">Headline</span>
                  <p className="font-medium">{aiInsights.headline}</p>
                </div>
                {aiInsights.key_takeaways && aiInsights.key_takeaways.length > 0 && (
                  <div>
                    <span className="text-xs text-muted-foreground uppercase tracking-wide">Key Takeaways</span>
                    <ul className="mt-1 space-y-1">
                      {aiInsights.key_takeaways.slice(0, 3).map((takeaway, idx) => (
                        <li key={idx} className="text-sm text-muted-foreground flex gap-2">
                          <span className="text-primary">•</span>
                          {takeaway}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="bg-card border border-border rounded-xl p-6">
            <h3 className="font-semibold mb-3">How to Post on LinkedIn</h3>
            <ol className="space-y-2 text-sm text-muted-foreground">
              <li className="flex gap-2">
                <span className="bg-primary/10 text-primary rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-xs font-medium">1</span>
                Click "Download All" to get all carousel slides
              </li>
              <li className="flex gap-2">
                <span className="bg-primary/10 text-primary rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-xs font-medium">2</span>
                Go to LinkedIn and create a new post
              </li>
              <li className="flex gap-2">
                <span className="bg-primary/10 text-primary rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-xs font-medium">3</span>
                Click the document icon and upload all images
              </li>
              <li className="flex gap-2">
                <span className="bg-primary/10 text-primary rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-xs font-medium">4</span>
                Copy the suggested caption and paste it
              </li>
              <li className="flex gap-2">
                <span className="bg-primary/10 text-primary rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-xs font-medium">5</span>
                Share and engage with your network!
              </li>
            </ol>
          </div>

          <div className="bg-card border border-border rounded-xl p-6">
            <h3 className="font-semibold mb-3 flex items-center justify-between">
              Suggested Caption
              <button
                onClick={copyCaption}
                className="text-xs text-primary hover:underline flex items-center gap-1"
              >
                {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copied ? "Copied!" : "Copy"}
              </button>
            </h3>
            <div className="bg-muted/50 rounded-lg p-4 text-sm whitespace-pre-line text-muted-foreground">
              {generateCaption()}
            </div>
          </div>
        </div>
      </div>

      {/* Hidden export container for high-res rendering */}
      <div className="fixed left-[-9999px] top-0 overflow-hidden">
        <div ref={exportRef}>
          <CarouselSlide 
            slide={slides[currentSlide]} 
            slideNumber={currentSlide + 1} 
            totalSlides={slides.length}
            forExport={true}
          />
        </div>
      </div>
    </div>
  );
};

export default LinkedInCarouselGenerator;
